import type { PropertyFilter } from './objects'
import type { ThreadFilter } from './threads'

/**
 * Every cache key in one place.
 *
 * The live stream invalidates queries it did not create, and a key spelled
 * slightly differently in two files is a screen that silently stops refreshing —
 * the worst failure this panel can have, because it looks exactly like a
 * conversation where nothing is happening.
 */
export const keys = {
  threads: (filter: ThreadFilter) =>
    [
      'threads',
      filter.mode ?? '',
      filter.unreadOnly === true,
      filter.waitingOnly === true,
      filter.query ?? '',
      filter.limit ?? 0,
    ] as const,
  anyThreadList: () => ['threads'] as const,
  thread: (id: string) => ['thread', id] as const,
  messages: (id: string) => ['thread', id, 'messages'] as const,
  aiRun: (id: string) => ['ai-run', id] as const,
  // Every field the request is narrowed by, and for the reason above turned
  // inside out: a key that leaves one out is a screen that stops refreshing on
  // purpose. This one left out both, so choosing a status re-rendered with a
  // byte-identical key, TanStack served the cached page, and no request was ever
  // issued -- the filter and the search box both looked broken while the server
  // behind them narrowed correctly all along.
  properties: (filter: PropertyFilter) =>
    ['properties', filter.status ?? '', filter.query ?? '', filter.limit ?? 0] as const,
  // Recording a check changes a count on the list as well as the property being
  // read, so the write invalidates the whole list rather than the page it happens
  // to have open.
  anyPropertyList: () => ['properties'] as const,
  property: (id: string) => ['property', id] as const,
  listingHistory: (id: string) => ['listing', id, 'history'] as const,
} as const
